/* Web Push helper shared by the CRM and the webmail.
 *
 * Platform notes that shape this code:
 *   iOS/iPadOS  Safari only offers Notification.requestPermission() when the
 *               site is running from the Home Screen, and only inside a user
 *               gesture. So we never ask on load - a button asks - and we can
 *               tell the user precisely why the option is missing.
 *   macOS       Safari 16.4+ works in a normal tab.
 *   Android     Chrome/Edge/Firefox work in a tab; installing is optional.
 *   Windows     Chrome/Edge deliver through WNS; nothing special needed.
 */

const BASE = process.env.REACT_APP_BACKEND_URL || "";

const b64ToU8 = (b64) => {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export function pushSupport() {
  if (!("serviceWorker" in navigator)) return { ok: false, why: "This browser has no service worker support." };
  if (!("PushManager" in window)) {
    if (isIOS() && !isStandalone())
      return { ok: false, ios: true, why: "On iPhone and iPad, add this app to your Home Screen first — Share → Add to Home Screen — then open it from there." };
    return { ok: false, why: "This browser does not support push notifications." };
  }
  if (isIOS() && !isStandalone())
    return { ok: false, ios: true, why: "On iPhone and iPad, open the app from your Home Screen to enable notifications." };
  if (!window.isSecureContext) return { ok: false, why: "Notifications need HTTPS." };
  return { ok: true };
}

export async function registerSW() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  } catch {
    return null;
  }
}

async function api(path, token, opts = {}) {
  const headers = { ...(opts.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (opts.json) headers["Content-Type"] = "application/json";
  const res = await fetch(`${BASE}/api/push${path}`, {
    method: opts.method || "GET",
    headers,
    body: opts.json ? JSON.stringify(opts.json) : undefined,
  });
  if (!res.ok) {
    let msg = `Request failed (${res.status}).`;
    try { const j = await res.json(); if (typeof j.detail === "string") msg = j.detail; } catch { /* keep default */ }
    throw new Error(msg);
  }
  return res.json();
}

export const pushStatus = (token) => api("/status", token);

/** Ask permission and register this device. Must be called from a click. */
export async function enablePush(token) {
  const sup = pushSupport();
  if (!sup.ok) throw new Error(sup.why);

  const { key, configured } = await api("/key", token);
  if (!configured || !key)
    throw new Error("Push is not set up on the server yet. Generate VAPID keys in the launcher.");

  const reg = (await navigator.serviceWorker.getRegistration("/")) || (await registerSW());
  if (!reg) throw new Error("The service worker could not be registered.");
  await navigator.serviceWorker.ready;

  const perm = await Notification.requestPermission();
  if (perm !== "granted")
    throw new Error(perm === "denied"
      ? "Notifications are blocked for this site. Allow them in your browser settings and try again."
      : "Notification permission was dismissed.");

  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    // A subscription made against a different VAPID key can never be decrypted
    // by this server, so replace it rather than keep a silent dud.
    const current = sub.options?.applicationServerKey;
    const same = current && btoa(String.fromCharCode(...new Uint8Array(current)))
      .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "") === key;
    if (!same) { try { await sub.unsubscribe(); } catch { /* ignore */ } sub = null; }
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToU8(key) });
  }

  const j = sub.toJSON();
  await api("/subscribe", token, {
    method: "POST",
    json: { endpoint: j.endpoint, keys: j.keys, ua: navigator.userAgent, label: deviceLabel() },
  });
  return true;
}

export async function disablePush(token) {
  const reg = await navigator.serviceWorker.getRegistration("/");
  const sub = reg && (await reg.pushManager.getSubscription());
  if (sub) {
    await api("/unsubscribe", token, { method: "POST", json: { endpoint: sub.endpoint } }).catch(() => {});
    try { await sub.unsubscribe(); } catch { /* ignore */ }
  }
  return true;
}

export const testPush = (token) => api("/test", token, { method: "POST" });

function deviceLabel() {
  const ua = navigator.userAgent;
  const os = /iPhone/.test(ua) ? "iPhone" : /iPad/.test(ua) ? "iPad"
    : /Android/.test(ua) ? "Android" : /Mac OS X/.test(ua) ? "Mac"
    : /Windows/.test(ua) ? "Windows" : "Device";
  const br = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome"
    : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "Browser";
  return `${os} · ${br}`;
}
